import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Send, Trash2, Save, ArrowLeft, Megaphone, RefreshCw, Link2, Star, Sparkles, Copy, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Eyebrow } from "@/components/site/primitives";

const TOKEN_KEY = "rvr_studio_token";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const TIMEZONES = [
  "Africa/Cairo",
  "Africa/Casablanca",
  "Asia/Dubai",
  "Asia/Riyadh",
  "Europe/London",
  "Europe/Paris",
  "America/New_York",
  "America/Los_Angeles",
  "UTC",
];

export default function Studio() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [passphrase, setPassphrase] = useState("");
  const [signingIn, setSigningIn] = useState(false);

  const [dispatches, setDispatches] = useState([]);
  const [letters, setLetters] = useState([]);
  const [crypticLine, setCrypticLine] = useState("");
  const [releaseWeekday, setReleaseWeekday] = useState(0);
  const [releaseHour, setReleaseHour] = useState(17);
  const [releaseTz, setReleaseTz] = useState("Africa/Cairo");
  const [nextTitle, setNextTitle] = useState("");
  const [nextFilm, setNextFilm] = useState("");
  const [nextWatch, setNextWatch] = useState("");
  const [platforms, setPlatforms] = useState({ spotify: "", apple: "", youtube: "", instagram: "" });
  const [subscribers, setSubscribers] = useState([]);
  const [post, setPost] = useState({
    title: "",
    tag: "DISPATCH",
    body: "",
    kind: "note",
    platform: "instagram",
    link_url: "",
    image_url: "",
  });
  const [publishing, setPublishing] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [announcing, setAnnouncing] = useState(false);
  const [tiktok, setTiktok] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [youtubeCount, setYoutubeCount] = useState(0);
  const [ytLastSync, setYtLastSync] = useState(null);
  const [ytSyncing, setYtSyncing] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [aiTeaserBusy, setAiTeaserBusy] = useState(false);
  const [aiQuoteBusy, setAiQuoteBusy] = useState(false);
  const [episodes, setEpisodes] = useState([]);
  const [episodeTitles, setEpisodeTitles] = useState({});
  const [savingEpisode, setSavingEpisode] = useState(null);

  const loadStudioData = useCallback(async () => {
    try {
      const [disRes, letRes, siteRes, subRes, ttRes, ytRes, quoteRes, epRes] = await Promise.all([
        api.get("/dispatches"),
        api.get("/letters"),
        api.get("/site/private"),
        api.get("/subscribers"),
        api.get("/tiktok/status"),
        api.get("/youtube/videos"),
        api.get("/studio/ai/quotes"),
        api.get("/episodes"),
      ]);
      setDispatches(disRes.data);
      setLetters(letRes.data);
      setSubscribers(subRes.data);
      setTiktok(ttRes.data);
      setYoutubeCount(ytRes.data.length);
      setYtLastSync(siteRes.data.youtube_last_sync || null);
      setQuotes(quoteRes.data);
      setEpisodes(epRes.data);
      setEpisodeTitles(
        Object.fromEntries(epRes.data.map((ep) => [ep.id, ep.title]))
      );
      setCrypticLine(siteRes.data.cryptic_line || "");
      setReleaseWeekday(siteRes.data.release_weekday ?? 0);
      setReleaseHour(siteRes.data.release_hour ?? 17);
      setReleaseTz(siteRes.data.release_tz || "Africa/Cairo");
      setNextTitle(siteRes.data.next_episode_title || "");
      setNextFilm(siteRes.data.next_episode_film || "");
      setNextWatch(siteRes.data.next_episode_watch_url || "");
      setPlatforms({
        spotify: siteRes.data.platforms?.spotify || "",
        apple: siteRes.data.platforms?.apple || "",
        youtube: siteRes.data.platforms?.youtube || "",
        instagram: siteRes.data.platforms?.instagram || "",
      });
    } catch {
      toast.error("Could not load studio data.");
    }
  }, []);

  useEffect(() => {
    if (token) loadStudioData();
  }, [token, loadStudioData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setSigningIn(true);
    try {
      const { data } = await api.post("/studio/login", { passphrase });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Welcome to the studio.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sign-in failed.");
    } finally {
      setSigningIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  const handlePublish = async (e) => {
    e.preventDefault();
    setPublishing(true);
    try {
      await api.post("/dispatches", {
        title: post.title.trim(),
        tag: post.tag.trim() || "DISPATCH",
        body: post.body.trim(),
        kind: post.kind,
        platform: post.kind === "link" ? post.platform : null,
        link_url: post.kind === "link" ? post.link_url.trim() : null,
        image_url: post.kind === "photo" ? post.image_url.trim() : null,
      });
      toast.success("Posted to the rolling feed.");
      setPost({ title: "", tag: "DISPATCH", body: "", kind: "note", platform: "instagram", link_url: "", image_url: "" });
      loadStudioData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Publish failed.");
    } finally {
      setPublishing(false);
    }
  };

  const handleDeleteDispatch = async (id) => {
    try {
      await api.delete(`/dispatches/${id}`);
      toast.success("Dispatch removed.");
      loadStudioData();
    } catch {
      toast.error("Could not remove dispatch.");
    }
  };

  const handleAiTeaser = async () => {
    setAiTeaserBusy(true);
    try {
      const { data } = await api.post("/studio/ai/teaser");
      setCrypticLine(data.cryptic_line);
      toast.success("Claude wrote the teaser — it's live on the site.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "The AI is off duty. Try again.");
    } finally {
      setAiTeaserBusy(false);
    }
  };

  const handleAiQuote = async () => {
    setAiQuoteBusy(true);
    try {
      await api.post("/studio/ai/quote");
      toast.success("Today's story quote is written.");
      loadStudioData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "The AI is off duty. Try again.");
    } finally {
      setAiQuoteBusy(false);
    }
  };

  const copyQuote = async (quote) => {
    try {
      await navigator.clipboard.writeText(quote);
      toast.success("Copied — paste it straight into your story.");
    } catch {
      toast.error("Copy failed — select the text manually.");
    }
  };

  const handleFeatureLetter = async (id) => {
    try {
      const { data } = await api.post(`/letters/${id}/feature`);
      toast.success(
        data.featured ? "Letter starred — it leads the next recap." : "Letter unstarred."
      );
      loadStudioData();
    } catch {
      toast.error("Could not update the letter.");
    }
  };

  const handleDeleteLetter = async (id) => {
    try {
      await api.delete(`/letters/${id}`);
      toast.success("Letter removed.");
      loadStudioData();
    } catch {
      toast.error("Could not remove letter.");
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await api.put("/site/config", {
        cryptic_line: crypticLine.trim(),
        release_weekday: Number(releaseWeekday),
        release_hour: Number(releaseHour),
        release_tz: releaseTz,
        next_episode_title: nextTitle,
        next_episode_film: nextFilm,
        next_episode_watch_url: nextWatch,
        platforms,
      });
      toast.success("Show settings saved.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save settings.");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveEpisodeTitle = async (ep) => {
    const title = (episodeTitles[ep.id] || "").trim();
    if (!title) {
      toast.error("Title cannot be empty.");
      return;
    }
    setSavingEpisode(ep.id);
    try {
      await api.put(`/episodes/${ep.id}/title`, { title });
      toast.success(`EP${ep.number} title saved.`);
      loadStudioData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save the title.");
    } finally {
      setSavingEpisode(null);
    }
  };

  const handleAnnounce = async () => {
    if (!window.confirm(`Email the premiere announcement to all ${subscribers.length} subscriber(s) now?`)) {
      return;
    }
    setAnnouncing(true);
    try {
      const { data } = await api.post("/studio/announce");
      toast.success(`Premiere email on its way to ${data.recipients} subscriber(s).`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send the announcement.");
    } finally {
      setAnnouncing(false);
    }
  };

  const handleTiktokConnect = async () => {
    try {
      const { data } = await api.get("/tiktok/connect");
      window.location.href = data.authorize_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start the TikTok connection.");
    }
  };

  const handleTiktokSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/tiktok/sync");
      toast.success(
        data.synced ? `Synced ${data.synced} TikTok video(s) into the feed.` : "Nothing new to sync."
      );
      loadStudioData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sync failed.");
    } finally {
      setSyncing(false);
    }
  };

  const handleYoutubeSync = async () => {
    setYtSyncing(true);
    try {
      const { data } = await api.post("/youtube/sync");
      toast.success(`Synced ${data.synced} YouTube video(s) into the feed.`);
      loadStudioData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "YouTube sync failed.");
    } finally {
      setYtSyncing(false);
    }
  };

  const handleDeleteSubscriber = async (id) => {
    try {
      await api.delete(`/subscribers/${id}`);
      toast.success("Subscriber removed.");
      loadStudioData();
    } catch {
      toast.error("Could not remove subscriber.");
    }
  };

  const inputClass =
    "mt-3 w-full border border-bone/15 bg-noir px-4 py-3 text-base font-light text-bone placeholder:text-zinc-600 focus:border-ruby focus:outline-none focus:ring-1 focus:ring-ruby";

  if (!token) {
    return (
      <main data-testid="studio-login-page" className="flex min-h-screen items-center justify-center px-6">
        <div className="w-full max-w-md border border-bone/10 bg-coal p-10">
          <Eyebrow>Studio access</Eyebrow>
          <h1 className="mt-4 font-display text-3xl font-light tracking-tight text-bone">
            The projection room.
          </h1>
          <p className="mt-4 text-sm font-light leading-relaxed text-zinc-400">
            Publish dispatches between episodes and update the weekly teaser.
          </p>
          <form onSubmit={handleLogin} className="mt-8 space-y-6" data-testid="studio-login-form">
            <div>
              <label htmlFor="studio-passphrase" className="font-mono text-xs uppercase tracking-[0.2em] text-smoke">
                Passphrase
              </label>
              <input
                id="studio-passphrase"
                data-testid="studio-passphrase-input"
                type="password"
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                className={inputClass}
                placeholder="••••••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={signingIn}
              data-testid="studio-login-button"
              className="w-full bg-ruby px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-ruby-bright disabled:opacity-50"
            >
              {signingIn ? "Checking…" : "Enter the studio"}
            </button>
          </form>
          <Link
            to="/"
            data-testid="studio-back-link"
            className="mt-6 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-smoke transition-colors hover:text-bone"
          >
            <ArrowLeft size={12} /> Back to the site
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="studio-dashboard" className="mx-auto max-w-4xl px-6 py-16">
      <div className="flex items-center justify-between">
        <div>
          <Eyebrow>Studio</Eyebrow>
          <h1 className="mt-3 font-display text-3xl font-light tracking-tight text-bone">
            The projection room
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <Link
            to="/"
            data-testid="studio-view-site-link"
            className="font-mono text-xs uppercase tracking-[0.2em] text-smoke transition-colors hover:text-bone"
          >
            View site
          </Link>
          <button
            onClick={handleLogout}
            data-testid="studio-logout-button"
            className="flex items-center gap-2 border border-bone/20 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-colors hover:border-ruby hover:text-ruby"
          >
            <LogOut size={12} /> Leave
          </button>
        </div>
      </div>

      <section className="mt-14 border border-bone/10 bg-coal p-8" data-testid="studio-tiktok-section">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-medium text-bone">Social sync</h2>
            <p className="mt-2 max-w-lg text-sm font-light text-zinc-400">
              Your latest YouTube uploads roll into the feed automatically — checked every 15
              minutes. Connect TikTok below to add it too.
            </p>
          </div>
          {tiktok?.connected ? (
            <button
              onClick={handleTiktokSync}
              disabled={syncing}
              data-testid="tiktok-sync-button"
              className="flex items-center gap-2 border border-ruby px-5 py-3 font-mono text-xs uppercase tracking-[0.15em] text-ruby transition-colors duration-300 hover:bg-ruby hover:text-white disabled:opacity-50"
            >
              <RefreshCw size={13} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Syncing…" : "Sync now"}
            </button>
          ) : (
            <button
              onClick={handleTiktokConnect}
              disabled={!tiktok?.configured}
              data-testid="tiktok-connect-button"
              className="flex items-center gap-2 bg-ruby px-5 py-3 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-ruby-bright disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Link2 size={13} /> Connect TikTok
            </button>
          )}
        </div>
        <div className="mt-6 border border-bone/10 bg-noir px-5 py-4" data-testid="tiktok-status">
          {!tiktok?.configured && (
            <p className="font-mono text-xs uppercase tracking-[0.15em] leading-relaxed text-smoke">
              Status: <span className="text-zinc-500">waiting for app keys</span> — the Connect
              button goes live as soon as the TikTok app key and secret are added.
            </p>
          )}
          {tiktok?.configured && !tiktok?.connected && (
            <p className="font-mono text-xs uppercase tracking-[0.15em] leading-relaxed text-smoke">
              Status: <span className="text-ruby">ready to connect</span> — press Connect TikTok
              and approve access on your account.
            </p>
          )}
          {tiktok?.connected && (
            <p className="font-mono text-xs uppercase tracking-[0.15em] leading-relaxed text-smoke">
              Status: <span className="text-bone">connected</span> · {tiktok.videos_cached} video(s)
              in the feed
              {tiktok.last_sync
                ? ` · last sync ${new Date(tiktok.last_sync).toLocaleString("en-GB")}`
                : ""}
            </p>
          )}
        </div>
        <div className="mt-4 border border-bone/10 bg-noir px-5 py-4" data-testid="youtube-status">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="font-mono text-xs uppercase tracking-[0.15em] leading-relaxed text-smoke">
              YouTube: <span className="text-bone">live via @nilefm</span> · {youtubeCount}{" "}
              video(s) in the feed
              {ytLastSync
                ? ` · last sync ${new Date(ytLastSync).toLocaleString("en-GB")}`
                : ""}
            </p>
            <button
              onClick={handleYoutubeSync}
              disabled={ytSyncing}
              data-testid="youtube-sync-button"
              className="flex items-center gap-2 border border-bone/20 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-bone transition-colors duration-300 hover:border-ruby hover:text-ruby disabled:opacity-50"
            >
              <RefreshCw size={13} className={ytSyncing ? "animate-spin" : ""} />
              {ytSyncing ? "Syncing…" : "Sync YouTube"}
            </button>
          </div>
        </div>
      </section>

      <section className="mt-10 border border-bone/10 bg-coal p-8" data-testid="studio-quotes-section">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-medium text-bone">Daily story quote</h2>
            <p className="mt-2 max-w-lg text-sm font-light text-zinc-400">
              Claude writes a relationship quote every morning at 9am Cairo time, inspired by the
              latest episode — copy it straight into your Instagram story. It also rolls into the
              feed as a quote card.
            </p>
          </div>
          <button
            onClick={handleAiQuote}
            disabled={aiQuoteBusy}
            data-testid="ai-quote-button"
            className="flex items-center gap-2 border border-ruby px-5 py-3 font-mono text-xs uppercase tracking-[0.15em] text-ruby transition-colors duration-300 hover:bg-ruby hover:text-white disabled:opacity-50"
          >
            <Sparkles size={13} /> {aiQuoteBusy ? "Writing…" : "Write today's now"}
          </button>
        </div>

        {quotes.length > 0 && (
          <div className="mt-6 border border-ruby/25 bg-noir p-6" data-testid="quote-today">
            <p className="font-display text-xl italic leading-snug text-bone">
              “{quotes[0].quote}”
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                {quotes[0].date}
                {quotes[0].film ? ` · inspired by ${quotes[0].film}` : ""}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => copyQuote(quotes[0].quote)}
                  data-testid="quote-copy-today"
                  className="flex items-center gap-2 border border-bone/20 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-bone transition-colors duration-300 hover:border-ruby hover:text-ruby"
                >
                  <Copy size={13} /> Copy for story
                </button>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/story-card?download=1`}
                  data-testid="quote-download-story"
                  className="flex items-center gap-2 border border-ruby px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-ruby transition-colors duration-300 hover:bg-ruby hover:text-white"
                >
                  <ImageIcon size={13} /> Story image
                </a>
              </div>
            </div>
          </div>
        )}

        {quotes.length > 1 && (
          <div className="mt-4 space-y-px border border-bone/10 bg-bone/10" data-testid="quote-history">
            {quotes.slice(1).map((q) => (
              <div key={q.id} className="flex items-start justify-between gap-4 bg-coal px-5 py-4">
                <div>
                  <p className="font-display text-base italic text-bone">“{q.quote}”</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                    {q.date}
                  </p>
                </div>
                <button
                  onClick={() => copyQuote(q.quote)}
                  data-testid={`quote-copy-${q.id}`}
                  className="mt-1 text-smoke transition-colors hover:text-ruby"
                  aria-label="Copy quote"
                >
                  <Copy size={15} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-10 border border-bone/10 bg-coal p-8" data-testid="studio-episodes-section">
        <h2 className="font-display text-xl font-medium text-bone">Episode canon titles</h2>
        <p className="mt-2 max-w-lg text-sm font-light text-zinc-400">
          Confirm the real title for each episode. These appear on the public episode list and in
          the recap emails. The latest episode is marked.
        </p>
        <div className="mt-6 space-y-px border border-bone/10 bg-bone/10">
          {episodes.map((ep) => (
            <div
              key={ep.id}
              className="flex flex-wrap items-center gap-3 bg-coal px-5 py-4"
              data-testid={`episode-row-${ep.number}`}
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ruby">
                EP{String(ep.number).padStart(2, "0")}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-smoke">
                {ep.film}
              </span>
              {ep.status === "latest" && (
                <span className="border border-ruby/40 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-ruby">
                  Latest
                </span>
              )}
              <div className="ml-auto flex min-w-0 flex-1 items-center gap-2 sm:flex-none">
                <input
                  value={episodeTitles[ep.id] ?? ""}
                  onChange={(e) =>
                    setEpisodeTitles({ ...episodeTitles, [ep.id]: e.target.value })
                  }
                  data-testid={`episode-title-input-${ep.number}`}
                  className="min-w-0 flex-1 border border-bone/15 bg-noir px-3 py-2 text-sm text-bone outline-none transition-colors focus:border-ruby sm:w-80"
                />
                <button
                  onClick={() => handleSaveEpisodeTitle(ep)}
                  disabled={savingEpisode === ep.id}
                  data-testid={`episode-title-save-${ep.number}`}
                  className="flex shrink-0 items-center gap-2 border border-bone/20 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-bone transition-colors duration-300 hover:border-ruby hover:text-ruby disabled:opacity-50"
                >
                  <Save size={13} /> {savingEpisode === ep.id ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14 border border-bone/10 bg-coal p-8" data-testid="studio-dispatch-form-section">
        <h2 className="font-display text-xl font-medium text-bone">Post to the rolling feed</h2>
        <p className="mt-2 text-sm font-light text-zinc-400">
          Notes, quotes, photos — or drop in anything you posted on Instagram or TikTok as a link card.
        </p>
        <form onSubmit={handlePublish} className="mt-6 space-y-5" data-testid="dispatch-form">
          <div className="grid gap-5 sm:grid-cols-[1fr_180px]">
            <input
              data-testid="dispatch-title-input"
              type="text"
              value={post.title}
              onChange={(e) => setPost({ ...post, title: e.target.value })}
              maxLength={140}
              placeholder="Title"
              className={inputClass}
              style={{ marginTop: 0 }}
            />
            <input
              data-testid="dispatch-tag-input"
              type="text"
              value={post.tag}
              onChange={(e) => setPost({ ...post, tag: e.target.value.toUpperCase() })}
              maxLength={40}
              placeholder="TAG"
              className={`${inputClass} font-mono text-xs uppercase tracking-[0.2em]`}
              style={{ marginTop: 0 }}
            />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="dispatch-kind" className="font-mono text-xs uppercase tracking-[0.2em] text-smoke">
                Post type
              </label>
              <select
                id="dispatch-kind"
                data-testid="dispatch-kind-select"
                value={post.kind}
                onChange={(e) => setPost({ ...post, kind: e.target.value })}
                className={inputClass}
              >
                <option value="note">Note</option>
                <option value="quote">Quote card</option>
                <option value="link">Link (Instagram / TikTok / YouTube)</option>
                <option value="photo">Photo</option>
              </select>
            </div>
            {post.kind === "link" && (
              <div>
                <label htmlFor="dispatch-platform" className="font-mono text-xs uppercase tracking-[0.2em] text-smoke">
                  Platform
                </label>
                <select
                  id="dispatch-platform"
                  data-testid="dispatch-platform-select"
                  value={post.platform}
                  onChange={(e) => setPost({ ...post, platform: e.target.value })}
                  className={inputClass}
                >
                  <option value="instagram">Instagram</option>
                  <option value="tiktok">TikTok</option>
                  <option value="youtube">YouTube</option>
                  <option value="other">Other</option>
                </select>
              </div>
            )}
          </div>
          {post.kind === "link" && (
            <input
              data-testid="dispatch-link-input"
              type="url"
              value={post.link_url}
              onChange={(e) => setPost({ ...post, link_url: e.target.value })}
              maxLength={300}
              placeholder="https://www.instagram.com/p/… or https://www.tiktok.com/…"
              className={inputClass}
            />
          )}
          {post.kind === "photo" && (
            <input
              data-testid="dispatch-image-input"
              type="url"
              value={post.image_url}
              onChange={(e) => setPost({ ...post, image_url: e.target.value })}
              maxLength={300}
              placeholder="https://… (image URL)"
              className={inputClass}
            />
          )}
          <textarea
            data-testid="dispatch-body-input"
            value={post.body}
            onChange={(e) => setPost({ ...post, body: e.target.value })}
            maxLength={4000}
            rows={5}
            placeholder="Write the note, letter or fragment…"
            className={`${inputClass} resize-none`}
            style={{ marginTop: 0 }}
          />
          <button
            type="submit"
            disabled={publishing || !post.title.trim() || !post.body.trim()}
            data-testid="dispatch-publish-button"
            className="flex items-center gap-3 bg-ruby px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-ruby-bright disabled:opacity-50"
          >
            <Send size={13} /> {publishing ? "Publishing…" : "Publish dispatch"}
          </button>
        </form>

        <div className="mt-8 space-y-px border border-bone/10 bg-bone/10" data-testid="studio-dispatch-list">
          {dispatches.map((d) => (
            <div key={d.id} className="flex items-start justify-between gap-4 bg-coal px-5 py-4">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ruby">
                  {d.tag}
                  {d.kind && d.kind !== "note" ? ` · ${d.kind}` : ""}
                  {d.platform ? ` · ${d.platform}` : ""}
                </p>
                <p className="mt-1 font-display text-base text-bone">{d.title}</p>
              </div>
              <button
                onClick={() => handleDeleteDispatch(d.id)}
                data-testid={`dispatch-delete-${d.id}`}
                className="mt-1 text-smoke transition-colors hover:text-ruby"
                aria-label={`Delete ${d.title}`}
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 border border-bone/10 bg-coal p-8" data-testid="studio-settings-section">
        <h2 className="font-display text-xl font-medium text-bone">Show settings</h2>
        <p className="mt-2 text-sm font-light text-zinc-400">
          The cryptic teaser, the weekly release schedule for the countdown, and your listen links.
        </p>
        <form onSubmit={handleSaveSettings} className="mt-6 space-y-6" data-testid="settings-form">
          <div>
            <label htmlFor="settings-cryptic" className="font-mono text-xs uppercase tracking-[0.2em] text-smoke">
              Next-week teaser
            </label>
            <textarea
              id="settings-cryptic"
              data-testid="teaser-cryptic-input"
              value={crypticLine}
              onChange={(e) => setCrypticLine(e.target.value)}
              maxLength={280}
              rows={3}
              className={`${inputClass} resize-none`}
            />
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-600">
              Never the title — the episode stays unburned. Claude rewrites it every Sunday after
              the premiere, or whenever you schedule a new secret episode.
            </p>
            <button
              type="button"
              onClick={handleAiTeaser}
              disabled={aiTeaserBusy}
              data-testid="ai-teaser-button"
              className="mt-3 flex items-center gap-2 border border-bone/20 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:border-ruby hover:text-ruby disabled:opacity-50"
            >
              <Sparkles size={13} /> {aiTeaserBusy ? "Writing…" : "Write it with AI"}
            </button>
          </div>
          <div className="grid gap-5 sm:grid-cols-3">
            <div>
              <label htmlFor="settings-weekday" className="font-mono text-xs uppercase tracking-[0.2em] text-smoke">
                Release day
              </label>
              <select
                id="settings-weekday"
                data-testid="release-weekday-select"
                value={releaseWeekday}
                onChange={(e) => setReleaseWeekday(e.target.value)}
                className={inputClass}
              >
                {WEEKDAYS.map((day, index) => (
                  <option key={day} value={index}>{day}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="settings-hour" className="font-mono text-xs uppercase tracking-[0.2em] text-smoke">
                Release hour
              </label>
              <select
                id="settings-hour"
                data-testid="release-hour-select"
                value={releaseHour}
                onChange={(e) => setReleaseHour(e.target.value)}
                className={inputClass}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="settings-tz" className="font-mono text-xs uppercase tracking-[0.2em] text-smoke">
                Timezone
              </label>
              <select
                id="settings-tz"
                data-testid="release-tz-select"
                value={releaseTz}
                onChange={(e) => setReleaseTz(e.target.value)}
                className={inputClass}
              >
                {TIMEZONES.map((zone) => (
                  <option key={zone} value={zone}>{zone.replace("_", " ")}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid gap-5 sm:grid-cols-3">
            {[
              { id: "spotify", label: "Spotify URL" },
              { id: "apple", label: "Apple Podcasts URL" },
              { id: "youtube", label: "YouTube URL" },
              { id: "instagram", label: "Instagram URL" },
            ].map((platform) => (
              <div key={platform.id}>
                <label htmlFor={`settings-${platform.id}`} className="font-mono text-xs uppercase tracking-[0.2em] text-smoke">
                  {platform.label}
                </label>
                <input
                  id={`settings-${platform.id}`}
                  data-testid={`platform-input-${platform.id}`}
                  type="url"
                  value={platforms[platform.id]}
                  onChange={(e) => setPlatforms({ ...platforms, [platform.id]: e.target.value })}
                  placeholder="https://…"
                  className={inputClass}
                />
              </div>
            ))}
          </div>
          <div className="border border-ruby/25 bg-noir p-6">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-ruby">
              The next episode — stays redacted until premiere
            </p>
            <p className="mt-2 text-sm font-light text-zinc-400">
              Set it here and the site reveals it automatically when the countdown hits zero
              (visible for 48 hours, then it goes dark again).
            </p>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="next-title" className="font-mono text-xs uppercase tracking-[0.2em] text-smoke">
                  Episode title
                </label>
                <input
                  id="next-title"
                  data-testid="next-title-input"
                  type="text"
                  value={nextTitle}
                  onChange={(e) => setNextTitle(e.target.value)}
                  maxLength={140}
                  placeholder="e.g. The One That Got Away"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="next-film" className="font-mono text-xs uppercase tracking-[0.2em] text-smoke">
                  Film
                </label>
                <input
                  id="next-film"
                  data-testid="next-film-input"
                  type="text"
                  value={nextFilm}
                  onChange={(e) => setNextFilm(e.target.value)}
                  maxLength={140}
                  placeholder="e.g. La La Land"
                  className={inputClass}
                />
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="next-watch" className="font-mono text-xs uppercase tracking-[0.2em] text-smoke">
                Watch URL (YouTube)
              </label>
              <input
                id="next-watch"
                data-testid="next-watch-input"
                type="url"
                value={nextWatch}
                onChange={(e) => setNextWatch(e.target.value)}
                maxLength={300}
                placeholder="https://youtu.be/…"
                className={inputClass}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={savingSettings || !crypticLine.trim()}
            data-testid="settings-save-button"
            className="flex items-center gap-3 border border-ruby px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-ruby transition-colors duration-300 hover:bg-ruby hover:text-white disabled:opacity-50"
          >
            <Save size={13} /> {savingSettings ? "Saving…" : "Save settings"}
          </button>
        </form>
      </section>

      <section className="mt-10 border border-bone/10 bg-coal p-8" data-testid="studio-subscribers-section">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-medium text-bone">Premiere list</h2>
            <p className="mt-2 text-sm font-light text-zinc-400">
              Everyone who asked to be notified when a new episode drops. The list is emailed
              automatically every premiere — Sundays at 17:00, Cairo time.
            </p>
          </div>
          <button
            onClick={handleAnnounce}
            disabled={announcing}
            data-testid="announce-button"
            className="flex items-center gap-2 border border-ruby px-5 py-3 font-mono text-xs uppercase tracking-[0.15em] text-ruby transition-colors duration-300 hover:bg-ruby hover:text-white disabled:opacity-50"
          >
            <Megaphone size={13} /> {announcing ? "Sending…" : "Announce now"}
          </button>
        </div>
        <div className="mt-6 space-y-px border border-bone/10 bg-bone/10" data-testid="studio-subscriber-list">
          {subscribers.length === 0 && (
            <p className="bg-coal px-5 py-6 text-sm font-light text-zinc-500">No one on the list yet.</p>
          )}
          {subscribers.map((sub) => (
            <div key={sub.id} className="flex items-center justify-between gap-4 bg-coal px-5 py-4">
              <p className="font-mono text-sm text-bone">
                {sub.email}
                {sub.active === false && (
                  <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                    unsubscribed
                  </span>
                )}
              </p>
              <button
                onClick={() => handleDeleteSubscriber(sub.id)}
                data-testid={`subscriber-delete-${sub.id}`}
                className="text-smoke transition-colors hover:text-ruby"
                aria-label={`Remove ${sub.email}`}
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 border border-bone/10 bg-coal p-8" data-testid="studio-letters-section">
        <h2 className="font-display text-xl font-medium text-bone">Letters received</h2>
        <div className="mt-6 space-y-px border border-bone/10 bg-bone/10" data-testid="studio-letter-list">
          {letters.length === 0 && (
            <p className="bg-coal px-5 py-6 text-sm font-light text-zinc-500">No letters yet.</p>
          )}
          {letters.map((letter) => (
            <div key={letter.id} className="flex items-start justify-between gap-4 bg-coal px-5 py-4">
              <div>
                <p className="font-display text-base italic text-bone">“{letter.question}”</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                  — {letter.name}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleFeatureLetter(letter.id)}
                  data-testid={`letter-star-${letter.id}`}
                  className={`mt-1 transition-colors ${
                    letter.featured ? "text-ruby" : "text-smoke hover:text-ruby"
                  }`}
                  aria-label={letter.featured ? "Unstar letter" : "Star letter for the recap"}
                  title={letter.featured ? "Featured in the next recap" : "Feature in the next recap"}
                >
                  <Star size={15} fill={letter.featured ? "currentColor" : "none"} />
                </button>
                <button
                  onClick={() => handleDeleteLetter(letter.id)}
                  data-testid={`letter-delete-${letter.id}`}
                  className="mt-1 text-smoke transition-colors hover:text-ruby"
                  aria-label={`Delete letter from ${letter.name}`}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
